import { useRef, useState, useEffect, useLayoutEffect } from 'react';

import { formatPrice } from 'utils/helpers';
import { CloseButton } from 'components/specified-buttons';

import * as Styles from './styles';

export const toCents = (value?: number | string | null) =>
  Math.round(Number(value || 0) * 100);

export const sumAmounts = (rows: { amount?: number | string | null }[] = []) =>
  rows.reduce((acc, row) => acc + toCents(row.amount), 0) / 100;

// Shown only when the records in a P&L line drawer do not add up to the line
// to the cent: the difference is never hidden.
export const LineDifference = ({
  total,
  lineAmount,
}: {
  total: number;
  lineAmount?: number;
}) => {
  if (lineAmount === undefined) {
    return null;
  }
  const differenceCents = toCents(lineAmount) - toCents(total);
  if (!differenceCents) {
    return null;
  }
  return (
    <Styles.Warning data-testid="pl-line-difference">
      {`The P&L line is ${formatPrice(lineAmount)}; these records add up to ${formatPrice(
        total
      )}. Difference: ${formatPrice(differenceCents / 100)}.`}
    </Styles.Warning>
  );
};

// Esc closes a P&L line drawer (the report drawer under it stays open).
export const useEscapeToClose = (opened: boolean, onClose?: () => void) => {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!opened) {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseRef.current?.();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [opened]);
};

const hasOwnText = (element: Element) =>
  ['IMG', 'svg', 'INPUT', 'BUTTON'].includes(element.tagName) ||
  Array.from(element.childNodes).some(
    (node) => node.nodeType === Node.TEXT_NODE && !!node.textContent?.trim()
  );

// The layer an element is painted in: its closest fixed-position ancestor
// (another drawer), or the page.
const layerOf = (element: Element) => {
  let current: Element | null = element;
  while (current && current !== document.body) {
    if (window.getComputedStyle(current).position === 'fixed') {
      return current;
    }
    current = current.parentElement;
  }
  return document.body;
};

// The visible text elements (or icons/buttons) that a drawer edge at `top`
// would cut through: in the layer right under the drawer (what the user
// sees), starting above the edge and ending below it.
const cutAt = (top: number, drawer: Element) => {
  const cut = new Set<Element>();
  if (typeof document.elementsFromPoint !== 'function') {
    return [];
  }
  const { left, right } = drawer.getBoundingClientRect();
  for (let x = left + 8; x < right - 8; x += 16) {
    const below = document
      .elementsFromPoint(x, top + 1)
      .filter((element) => !drawer.contains(element));
    if (!below.length) {
      continue;
    }
    const layer = layerOf(below[0]);
    below.forEach((element) => {
      if (layerOf(element) !== layer || !hasOwnText(element)) {
        return;
      }
      const rect = element.getBoundingClientRect();
      if (rect.top < top - 0.5 && rect.bottom > top + 0.5) {
        cut.add(element);
      }
    });
  }
  return Array.from(cut).map((element) => element.getBoundingClientRect());
};

// A line drawer as tall as its content. Its top never covers the event
// report's header (title, dates, Save PDF stay readable) and never cuts
// through a line of text behind it: the edge moves up to cover that text
// whole, or down below it when there is no room above. Long lists scroll
// inside. `ref` goes on the drawer's content wrapper.
export const useFitDrawerTop = (mobile: boolean) => {
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const [top, setTop] = useState<number>();
  const history = useRef<number[]>([]);
  const padding = mobile ? 24 : 40;
  const base = mobile ? 0.1 : 0.15;

  const measure = useRef<() => void>(() => undefined);
  measure.current = () => {
    const drawer = node?.parentElement;
    if (!node || !drawer) {
      return;
    }
    const height = window.innerHeight;
    const header = Array.from(
      document.querySelectorAll('[data-testid="event-report-header"]')
    ).pop();
    const headerBottom = header ? header.getBoundingClientRect().bottom + 8 : 0;
    const minTop = Math.max(height * base, headerBottom);
    let next = Math.max(minTop, height - node.offsetHeight - 2 * padding);
    for (let i = 0; i < 60; i++) {
      const cut = cutAt(next, drawer);
      if (!cut.length) {
        break;
      }
      const above = Math.min(...cut.map((rect) => rect.top)) - 4;
      next =
        above >= minTop
          ? above
          : Math.max(...cut.map((rect) => rect.bottom)) + 8;
    }
    next = Math.round(next);
    // Never flip back and forth between two tops (e.g. a scrollbar that
    // comes and goes): once a value repeats, the higher top wins and stays.
    const [previous, beforePrevious] = history.current;
    if (next !== previous && next === beforePrevious) {
      next = Math.min(next, previous);
    }
    if (next !== previous) {
      history.current = [next, previous];
    }
    setTop((current) => (current === next ? current : next));
  };

  // Re-measure after every render (cheap; the state only changes when the
  // top really moves) ...
  useLayoutEffect(() => measure.current());

  // ... and when the content or the window resizes, subscribed once per
  // mounted drawer.
  useEffect(() => {
    if (!node) {
      return undefined;
    }
    const onResize = () => measure.current();
    window.addEventListener('resize', onResize);
    const observer =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(onResize);
    observer?.observe(node);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, [node]);

  return {
    ref: setNode,
    topOffset: top === undefined ? `${base * 100}%` : `${top}px`,
  };
};

// A line drawer's header: the P&L line's own name, the event underneath in
// muted text, and the close button where DrawerHeader puts it.
export const LineDrawerHeader = ({
  title,
  onClose,
  subtitle,
}: {
  title?: string;
  subtitle?: string;
  onClose?: () => void;
}) => (
  <Styles.Header data-testid="line-drawer-header">
    <Styles.HeaderText>
      <h2 title={title}>{title}</h2>
      {subtitle && <p title={subtitle}>{subtitle}</p>}
    </Styles.HeaderText>
    {onClose && <CloseButton onClose={onClose} />}
  </Styles.Header>
);
